import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Mail } from "lucide-react";

export default function AdminContacts() {
  const [msgs, setMsgs] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/contact").then(({ data }) => setMsgs(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success("Deleted.");
      setOpen(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-contacts-page">
      <h1 className="font-fredoka text-4xl" style={{ color: "#2D3055" }}>Messages</h1>
      <p className="mt-1 mb-8" style={{ color: "#4A4E6D" }}>{msgs.length} note{msgs.length !== 1 && "s"} from your readers.</p>

      <div className="grid md:grid-cols-2 gap-4">
        {msgs.map((m) => (
          <button
            key={m.id}
            onClick={() => setOpen(m)}
            className="warm-card p-5 text-left hover:-translate-y-1 transition-transform"
            data-testid={`msg-card-${m.id}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: "#FFD93D", color: "#2D3055" }}>
                <Mail size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-fredoka truncate" style={{ color: "#2D3055" }}>{m.name}</p>
                <p className="text-xs truncate" style={{ color: "#7E82A1" }}>{m.email}</p>
                <p className="text-sm mt-2 line-clamp-2" style={{ color: "#4A4E6D" }}>
                  {m.subject && <span className="font-fredoka" style={{ color: "#2D3055" }}>{m.subject}: </span>}
                  {m.message}
                </p>
                <p className="text-xs mt-2" style={{ color: "#7E82A1" }}>
                  {new Date(m.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </button>
        ))}
        {msgs.length === 0 && (
          <div className="paper-card p-10 text-center md:col-span-2">
            <p className="font-fredoka text-xl" style={{ color: "#2D3055" }}>No messages yet.</p>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(61,64,91,0.5)" }} data-testid="msg-modal">
          <div className="warm-card w-full max-w-xl max-h-[90vh] overflow-auto p-8">
            <p className="font-caveat text-xl" style={{ color: "#FF6B9D" }}>
              {new Date(open.created_at).toLocaleString()}
            </p>
            <h2 className="font-fredoka text-3xl mt-1" style={{ color: "#2D3055" }}>{open.subject || "(no subject)"}</h2>
            <p className="mt-2 text-sm" style={{ color: "#4A4E6D" }}>
              From <span className="font-fredoka" style={{ color: "#2D3055" }}>{open.name}</span> —{" "}
              <a href={`mailto:${open.email}`} style={{ color: "#6EC4E8" }}>{open.email}</a>
            </p>
            <div className="mt-6 prose-warm font-nunito whitespace-pre-wrap">{open.message}</div>
            <div className="mt-8 flex gap-3">
              <a href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject || "your note")}`} className="btn-terracotta" data-testid="msg-reply-btn">
                <Mail size={16} /> Reply
              </a>
              <button onClick={() => remove(open.id)} className="btn-ghost-warm" style={{ color: "#c1554b" }} data-testid="msg-delete-btn">
                <Trash2 size={16} /> Delete
              </button>
              <button onClick={() => setOpen(null)} className="btn-ghost-warm ml-auto" data-testid="msg-close-btn">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
